import { Image, Watermark } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import entete from "../../../../assets/img/entete_pool.png";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { frenchDate, frenchDateSimple, resolveSiteProduction } from "../../../../variables/functions";
import { API_URL, TOKEN } from "../../../../variables/constants";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import BarreTop from "../../../../components/docs/BarreTop";
import { DocumentFooter } from "../../../../components/docs/DocumentFooter";
import { getCompagnieLogo } from "../../../../hooks/useFilesHooks";
import AntidateBadge from "./AntidateBadge";
import DuplicataStamp from "./DuplicataStamp";
import AnnexeFlotte from "./AnnexeFlotte";

const resolveFractionnementLabels = (fractionnement) => {
  const defaultDisplay = "Annuel";
  const defaultBase = "ANNUEL";

  if (!fractionnement) {
    return { display: defaultDisplay, base: defaultBase };
  }

  const directLabel = fractionnement?.libelleFractionnement || fractionnement?.nom;
  if (directLabel && directLabel.trim()) {
    const trimmed = directLabel.trim();
    return { display: trimmed, base: trimmed.toUpperCase() };
  }

  const periode = (fractionnement?.periode || "").toUpperCase();
  const moisMin = Number(fractionnement?.moisMin);

  if (periode === "MOIS") {
    const mapping = {
      12: "Annuel",
      6: "Semestriel",
      3: "Trimestriel",
      2: "Bimestriel",
      1: "Mensuel",
    };

    if (Number.isFinite(moisMin) && mapping[moisMin]) {
      const label = mapping[moisMin];
      return { display: label, base: label.toUpperCase() };
    }

    if (Number.isFinite(moisMin) && moisMin > 0) {
      const plural = moisMin > 1 ? "mois" : "mois";
      const label = `${moisMin} ${plural}`;
      return { display: label, base: label.toUpperCase() };
    }
  }

  if (periode === "JOUR") {
    if (Number.isFinite(moisMin) && moisMin > 0) {
      const plural = moisMin > 1 ? "jours" : "jour";
      const label = `${moisMin} ${plural}`;
      return { display: label, base: label.toUpperCase() };
    }
  }

  return { display: defaultDisplay, base: defaultBase };
};

const computeFractionnementDetails = (contrat, primes) => {
  const fractionnement = contrat?.fractionnement;
  const { display, base } = resolveFractionnementLabels(fractionnement);

  const parsedNombre = Number(fractionnement?.nombre);
  let count = Number.isFinite(parsedNombre) && parsedNombre > 0 ? parsedNombre : 1;
  // Avenant d'ordre TARIFANT (OE) = se règle EN PLEIN (le différentiel/l'avoir est réglé en une fois) →
  // jamais fractionné, même si le contrat de base l'était.
  if (contrat?.avenant?.code === 'OE') count = 1;
  const hasFractionnement = count > 1;

  const parsedRate = Number(primes?.fractionnement);
  const rate = Number.isFinite(parsedRate) ? parsedRate : null;

  // Prime nette = celle de l'APERÇU (primes) en priorité : elle reflète le bonus/malus appliqué,
  // contrairement à la valeur stockée du contrat qui peut dater d'une création sans bonus.
  const primeNetteAnnuelle = Number(primes?.primeNette ?? contrat?.primeNette) || 0;
  const primeTotaleAnnuelle = Number(primes?.primeTotaleAnnuelle) || 0;
  const accessoires = Number(primes?.accessoires ?? contrat?.accessoires) || 0;

  let primeNetteFractionnee = null;
  if (hasFractionnement) {
    const provided = Number(contrat?.primeNetteFractionnee);
    if (Number.isFinite(provided)) {
      primeNetteFractionnee = provided;
    } else {
      primeNetteFractionnee = (primeNetteAnnuelle + accessoires) / count;
    }
  }

  const primeTotaleFractionnee = hasFractionnement ? primes?.primeTotale : null;

  const echeanceLabel = count > 1 ? `${count} échéances` : "1 échéance";
  const modalite = hasFractionnement
    ? `${echeanceLabel}${rate !== null ? ` - ${rate}%` : ""}`
    : "Paiement unique";

  const summary = hasFractionnement
    ? `${display || "Fractionné"} (${echeanceLabel}${rate !== null ? ` - ${rate}%` : ""})`
    : `${display || "Annuel"} (paiement unique)`;

  return {
    base,
    display: display || "Annuel",
    summary,
    modalite,
    hasFractionnement,
    count,
    rate,
    primeNetteAnnuelle,
    primeTotaleAnnuelle,
    primeNetteFractionnee,
    primeTotaleFractionnee,
  };
};

const DTRFFormatProposition = React.forwardRef((props, ref) => {
  const [date, setDate] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [primes, setPrimes] = useState("");
  const avenantCode = props?.contrat?.avenant?.code;
  const avenantEffectiveDate =
    props?.contrat?.avenant?.dateEffet ||
    props?.contrat?.avenant?.dateEffetAvenant ||
    props?.contrat?.dateEffetAvenant ||
    null;
  const shouldDisplayAvenantDate =
    ["AG", "DG"].includes(avenantCode || "") && avenantEffectiveDate;

  useEffect(() => {
    console.log("The contrat: ", props?.contrat);
    // Proposition (AN, RN, PR, IN, AG, DG, TG, RV…) : on passe par /preview/{codeId} qui dispatche
    // par code avenant et reflète les VRAIS montants de la proposition (bonus/malus, prorata…).
    // ⚠ « AN » inclus : sinon l'affaire nouvelle passait par /new-preview avec un payload partiel
    // (sans bonus/police) → le bonus n'était pas appliqué → écart aperçu / proposition.
    const PROPOSITION_AVENANT_CODES = ['AN', 'RN', 'PR', 'IN', 'AG', 'DG', 'TG', 'RV', 'CV'];
    const isAvenantProposition = PROPOSITION_AVENANT_CODES.includes(props?.contrat?.avenant?.code)
      && props?.contrat?.statutContrat === 'EN_PROPOSITION'
      && props?.contrat?.codeId;

    if (isAvenantProposition) {
      axiosInstance.get(`/api/contrat/preview/${props.contrat.codeId}`).then((res) => {
        setPrimes(res.data);
      }).catch((err) => {
        console.log(err);
        toast("Une erreur est survenue");
      });
    } else {
      let cData = {
        dateEffet: props?.contrat?.dateEffet,
        engins: props?.contrat?.engins,
        fractionnement: props?.contrat?.fractionnement,
        // Reprend les frais accessoires ENREGISTRÉS (incluant un éventuel override),
        // pour que le recalcul de l'aperçu ne réaffiche pas la valeur paramétrée par défaut.
        accessoiresEdites: props?.contrat?.accessoires,
      };
      axiosInstance.post(`/api/contrat/new-preview`, cData).then((res) => {
        console.log("PREVIEW: ", res.data);
        setPrimes(res.data);
      }).catch((err) => {
        console.log(err);
        toast("Une erreur est survenue");
      });
    }

    if (props?.contrat?.engins?.length === 1) {
      var dts = [];

      var enginCodeId = "";
      props.contrat?.engins &&
        (enginCodeId = props?.contrat?.engins[0]?.codeId);

      axiosInstance
        .get(
          `/api/sous-garantie/compulsory-guaranties/${enginCodeId}`,
        )
        .then((res) => {
          // console.log("Dataaaaa: ", res.data);
          res.data.forEach((cg, cgi) => {
            dts.push({
              key: `${cgi + dts.length}`,
              garantie: `${cg?.nom}`,
              capital: cg?.capital
                ? cg?.capital
                : 0,
              franchise: 0,
              bns: 0,
              reduc_comm: 0,
              reduc_flotte: 0,
              prime_nette: cg?.primeNette,
            });
          });

          props?.contrat?.engins?.forEach((eng) => {
            eng.sousGaranties.forEach((sousGarantie, idx) => {
              if (
                sousGarantie?.garantie?.nom === "Incendie" ||
                sousGarantie?.garantie?.nom === "Vol"
              ) {
                var pNette = !eng.valeurDeclaree
                  ? sousGarantie?.primeNette
                  : sousGarantie?.tauxPCent
                    ? (sousGarantie?.tauxPCent * eng.valeurDeclaree) / 100
                    : eng.tauxPMille
                      ? (sousGarantie?.tauxPMille * eng.valeurDeclaree) / 1000
                      : 0.0;

                dts.push({
                  key: `${idx + dts.length}`,
                  garantie: `${sousGarantie?.garantie?.nom === "Individuel Conducteur"
                    ? sousGarantie?.nom
                    : sousGarantie?.garantie?.nom
                    }`,
                  capital: eng.valeurDeclaree
                    ? eng.valeurDeclaree
                    : sousGarantie?.garantie?.capital
                      ? sousGarantie?.garantie?.capital
                      : 0,
                  franchise: 0,
                  bns: 0,
                  reduc_comm: 0,
                  reduc_flotte: 0,
                  prime_nette: pNette,
                });
              } else {
                dts.push({
                  key: `${idx + dts.length}`,
                  garantie: `${sousGarantie?.garantie?.nom === "Individuel Conducteur"
                    ? sousGarantie?.nom
                    : sousGarantie?.garantie?.nom
                    }`,
                  capital: sousGarantie.tauxPCent
                    ? (sousGarantie.primeNette * 100) /
                    sousGarantie.tauxPCent
                    : sousGarantie.tauxPMille
                      ? (sousGarantie.primeNette * 1000) /
                      sousGarantie.tauxPMille
                      : 0,
                  franchise: 0,
                  bns: 0,
                  reduc_comm: 0,
                  reduc_flotte: 0,
                  prime_nette: sousGarantie.primeNette
                });
              }
            });
          });
          console.log("DTSData: ", dts);
        })
        .catch((err) => {
          console.log(err);
        });

    }
    let dte = new Date();
    setDate(dte.toISOString());
    if (props?.contrat?.intermediaire) {
      if (props?.contrat?.intermediaire?.agence?.signature) {
        fetch(
          `${API_URL}/api/agence/signature/${props?.contrat?.intermediaire?.agence?.signature.split("/")[1]}`,
          {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
              "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
              "Access-Control-Allow-Headers":
                "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
            },
          },
        )
          .then(async (response) => {
            // convert byte to file
            const imageBytes = await response.arrayBuffer();
            var blob = new Blob([imageBytes], { type: "image/png" });
            var imageUrl = URL.createObjectURL(blob);
            setImagePreviewUrl(imageUrl);
            var file = new File(
              [blob],
              props?.contrat?.intermediaire?.agence?.signature.split("/")[1],
              {
                type: "image/png",
              },
            );
            console.log("File: ", file);

            setTimeout(() => {
              console.log("Preview: ", { imagePreviewUrl });
            }, 1000);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else if (
      props?.contrat?.producteur != null &&
      props?.contrat?.producteur?.agence?.signature
    ) {
      fetch(
        `${API_URL}/api/agence/signature/${props?.contrat?.producteur?.agence?.signature.split("//")[2]}`,
        {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
            "Access-Control-Allow-Headers":
              "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
          },
        },
      )
        .then(async (response) => {
          // convert byte to file
          const imageBytes = await response.arrayBuffer();
          var blob = new Blob([imageBytes], { type: "image/png" });
          var imageUrl = URL.createObjectURL(blob);
          setImagePreviewUrl(imageUrl);
          var file = new File(
            [blob],
            props?.contrat?.producteur?.agence?.signature.split("//")[2],
            {
              type: "image/png",
            },
          );
          console.log("File: ", file);

          setTimeout(() => {
            console.log("Preview: ", { imagePreviewUrl });
          }, 1000);
        })
        .catch((error) => {
          console.log(error);
        });
    }
    if (props?.contrat?.organisme?.logo) {
      console.log("Logo: ", props?.contrat?.organisme?.logo);

      getCompagnieLogo(props?.contrat?.organisme?.code)
        .then(async (response) => {
          // convert byte to file
          const imageBytes = await response.arrayBuffer();
          var blob = new Blob([imageBytes], { type: "image/png" });
          var imageUrl = URL.createObjectURL(blob);
          setLogoPreviewUrl(imageUrl);
          var file = new File(
            [blob],
            props?.contrat?.organisme?.logo?.split("/")[1],
            {
              type: "image/png",
            },
          );
          console.log("File: ", file);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [props?.contrat, imagePreviewUrl]);

  const fractionnementDetails = computeFractionnementDetails(props?.contrat, primes);

  // Annexe flotte. Pour un avenant d'incorporation (IN), on produit 4 annexes :
  // 1-2 « Incorporation » (engins incorporés seuls) puis 3-4 « Situation de la flotte »
  // (toute la flotte). `recentlyAdded` est posé par previewIncorporation à partir de
  // contrat.incorpores ; repli par appariement d'immatriculation. Si aucun incorporé
  // n'est identifiable (donnée perdue), enginsIncorpores reste vide → on ne montre que la
  // situation de la flotte, renumérotée 1 & 2 (cf. JSX plus bas).
  const enginsFlotte = primes?.engins || [];
  const enginsIncorpores = (() => {
    if (avenantCode !== "IN") return [];
    const flagged = enginsFlotte.filter((e) => e?.recentlyAdded);
    if (flagged.length > 0) return flagged;
    const incorpImmats = new Set(
      (props?.contrat?.incorpores || []).map((e) => `${e?.immatriculation || ""}`.trim().toUpperCase())
    );
    if (incorpImmats.size === 0) return [];
    return enginsFlotte.filter((e) => incorpImmats.has(`${e?.immatriculation || ""}`.trim().toUpperCase()));
  })();
  const annexeNumeroPolice = props.contrat?.numeroAvenant || props.contrat?.numeroPoliceP || props.contrat?.numeroPoliceM;
  const annexeAssure = props.contrat?.souscripteur?.prenom
    ? `${props.contrat?.souscripteur?.nom} ${props.contrat?.souscripteur?.prenom}`
    : props.contrat?.souscripteur?.nom;
  const annexeShowLogo = props?.contrat?.organisme?.typeOrganisme !== "POOL";
  const {
    base: fractionnementBareme,
    display: fractionnementTypeLabel,
    summary: fractionnementSummary,
    modalite: fractionnementModalite,
    hasFractionnement,
    primeNetteAnnuelle,
    primeTotaleAnnuelle,
    primeNetteFractionnee,
  } = fractionnementDetails;

  const bareme = fractionnementBareme || "ANNUEL";

  // Cible du BNS : « RC » si seule la RC/SURPRIME est éligible (cas courant), sinon « les garanties
  // éligibles » (le BNS est paramétrable par garantie via le flag bonus — repli RC/SURPRIME).
  const _isRcNomBns = (nom) => { const n = (nom || "").toUpperCase(); return n.startsWith("RC") || n.startsWith("SURPRIME"); };
  const _bonusGaranties = (primes?.engins?.length
    ? primes.engins.flatMap((e) => e?.compulsoryGuaranties || [])
    : (primes?.compulsoryGuaranties || [])
  ).filter((g) => (g?.bonus != null ? g.bonus : _isRcNomBns(g?.nom)));
  const bnsCible = (_bonusGaranties.length > 0 && _bonusGaranties.every((g) => _isRcNomBns(g?.nom)))
    ? "RC" : "les garanties éligibles";

  // Libellé avec signe : positif = BONUS (réduit la base éligible → signe "-") ; négatif = MALUS ("+").
  const bonusMalusVal = primes?.bonusMalus;
  const bonusMalusText = typeof bonusMalusVal === "number" && bonusMalusVal !== 0
    ? `(${bonusMalusVal > 0 ? "BONUS" : "MALUS"} sur ${bnsCible}) ${bonusMalusVal > 0 ? "-" : "+"} ${displayNumbers(Math.abs(bonusMalusVal))}%`
    : "0%";

  const primeNetteFractionneeDisplay = hasFractionnement && primeNetteFractionnee !== null
    ? displayNumbers(primeNetteFractionnee)
    : hasFractionnement
      ? "-"
      : displayNumbers(primeNetteAnnuelle);

  const fractionnementDisplay = fractionnementSummary || (hasFractionnement ? fractionnementTypeLabel : "Paiement unique");
  const echeanceDisplay = hasFractionnement ? fractionnementModalite : "Paiement unique";

  // ── Décomposition RC / autres / BNS / flotte ────────────────────────────────
  // Ordre de calcul backend : rc → ×(1 − bonusMalus/100) → ×(1 − flotte/100).
  //   rcDefaut = RC brute (avant BNS et flotte) ; rcFinal = RC nette (après les deux).
  const isFlotte = props?.contrat?.engins?.length > 1;
  const flotteTaux = isFlotte ? Number(primes?.flotte ?? 0) : 0;
  const rcDefautN = Number(primes?.rcDefaut ?? 0);
  const rcFinalN = Number(primes?.rcFinal ?? 0);
  const bonusRate = Number(primes?.bonusMalus ?? 0);
  // BNS et flotte sont SOMMÉS puis appliqués une seule fois (cf. backend) : chaque montant
  // se calcule sur la MÊME assiette réductible (rcDefaut), pas en cascade. Ainsi
  // bonusMontant + reductionFlotteMontant = rcDefaut − rcFinal exactement.
  const bonusMontant = rcDefautN * bonusRate / 100;                 // effet BNS (sur l'assiette réductible)
  const reductionFlotteMontant = Math.max(0, rcDefautN * flotteTaux / 100); // flotte (sur l'assiette réductible)
  const primeNetteAvecFlotte = Number(primes?.primeNette ?? props?.contrat?.primeNette) || 0;
  // Prime nette BRUTE = prime nette finale + (RC brute − RC nette) = pn + rcDefaut (avant flotte ET BNS).
  // Ainsi l'équation tient : brute − réduction flotte − bonusMontant = prime nette finale
  // (bonusMontant < 0 pour un malus → s'ajoute ; > 0 pour un bonus → se retranche).
  const primeNetteSansFlotte = primeNetteAvecFlotte + (rcDefautN - rcFinalN);
  const hasFlotteReduction = isFlotte && flotteTaux > 0 && reductionFlotteMontant > 0;
  // BNS : libellé existant (BONUS/MALUS sur RC + taux) + le montant
  const bnsMontantText = bonusRate === 0
    ? bonusMalusText
    : `${bonusMalusText} (${displayNumbers(Math.abs(bonusMontant))})`;

  const ligneBNS = { key: "bns", libelle: "BNS", montant: bnsMontantText };

  const reduData = hasFlotteReduction
    ? [
        { key: "1", libelle: "Fractionnement", montant: fractionnementSummary, align: "left" },
        {
          key: "2",
          libelle: "Prime nette annuelle (sans réduction flotte ni BNS)",
          montant: `${displayNumbers(primeNetteSansFlotte)}`,
        },
        {
          key: "3",
          libelle: (
            <span>
              Réduction flotte{" "}
              <span style={{ fontWeight: 400, fontStyle: "italic" }}>(sur les garanties éligibles)</span>
            </span>
          ),
          montant: `${displayNumbers(flotteTaux)} %  (− ${displayNumbers(reductionFlotteMontant)})`,
          highlight: true,
        },
        ligneBNS,
        {
          key: "5",
          libelle: hasFractionnement
            ? "Prime nette fractionnée (avec réduction flotte et BNS)"
            : "Prime nette annuelle (avec réduction flotte et BNS)",
          montant: primeNetteFractionneeDisplay,
          highlight: true,
        },
      ]
    : [
        { key: "1", libelle: "Fractionnement", montant: fractionnementSummary, align: "left" },
        { key: "2", libelle: "Réduction flotte", montant: isFlotte ? `${displayNumbers(flotteTaux)}%` : "0%" },
        ligneBNS,
        {
          key: "4",
          libelle: hasFractionnement ? "Prime nette fractionnée (par échéance)" : "Prime nette annuelle",
          montant: primeNetteFractionneeDisplay,
        },
      ];

  // Montants de l'APERÇU (primes) en priorité (bonus/malus appliqué), repli sur le contrat stocké.
  const effPrimeNette = primes?.primeNette != null ? primes.primeNette : props?.contrat?.primeNette;
  const effAccessoires = primes?.accessoires != null ? primes.accessoires : props?.contrat?.accessoires;
  // Taxes et Prime Totale dérivées de la MÊME base (prime nette + accessoires affichés),
  // sinon on mélangeait l'incrément (avenant d'incorporation) avec le total flotte du contrat
  // => Prime Totale ≠ Prime Nette + Coût de pièces + Taxes.
  const effTaxes = primes?.taxes != null
    ? primes.taxes
    : ((effPrimeNette || 0) + (effAccessoires || 0)) * 0.06;
  const effPrimeTotale = primes?.primeTotale != null
    ? primes.primeTotale
    : (effPrimeNette || 0) + (effAccessoires || 0) + (effTaxes || 0);
  const primeData = [
    {
      key: "1",
      libelle: "Prime Nette",
      montant: `${displayNumbers(effPrimeNette)}`,
    },
    {
      key: "2",
      libelle: "Coût de pièces",
      montant: `${displayNumbers(effAccessoires)}`,
    },
    {
      key: "3",
      libelle: "Taxes",
      montant: `6% (${displayNumbers(effTaxes)})`,
    },
    {
      key: "4",
      // Avenant OE : préciser « à encaisser » (prime positive) ou « à ristourner » (avoir, négatif).
      libelle: props?.contrat?.avenant?.code === 'OE'
        ? (Number(effPrimeTotale) < 0 ? "Prime à ristourner" : "Prime à encaisser")
        : "Prime Totale",
      montant: (props?.contrat?.avenant?.code === 'OE' && Number(effPrimeTotale) < 0)
        ? `${displayNumbers(Math.abs(Number(effPrimeTotale)))}`
        : `${displayNumbers(effPrimeTotale)}`,
    },
  ];

  // La colonne gauche (réductions / décomposition) a plus de lignes que la droite
  // (totaux financiers) → on complète la droite par des cellules vides discrètes.
  while (primeData.length < reduData.length) {
    primeData.push({ key: `pad-${primeData.length}`, libelle: "", montant: "", empty: true });
  }

  const titreSouscripteur =
    props.contrat?.souscripteur?.typePersonne === "PHYSIQUE"
      ? props.contrat?.souscripteur?.genre === "MASCULIN"
        ? "M."
        : props.contrat?.souscripteur?.genre === "FEMININ"
          ? "Mme"
          : ""
      : "Société";

  return (
    <>
      {props?.contrat.engins && props?.contrat.engins.length === 1 ? (
        <div className="col-12" ref={ref}>
          <Watermark
            content="PROPOSITION"
            zIndex={20}
            font={"bold 100px Arial"}
          >
            <div className="card"
              style={{ position: "relative", minHeight: "100vh" }}
            >
              <AntidateBadge contrat={props.contrat} variant="inset" />
              <DuplicataStamp contrat={props.contrat} />
              <div style={{ flexShrink: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0px 0px",
                  }}
                >
                  {props?.contrat?.organisme?.typeOrganisme !== "POOL" && <Image
                    src={logoPreviewUrl}
                    style={{
                      width: "100%",
                      height: "105px",
                    }}
                  />}
                  <Image
                    src={entete}
                    style={{
                      width: "100%",
                      height: "105px",
                    }}
                  />
                </div>

                <BarreTop />
              </div>
              <div
                style={{
                  padding: "1rem 1rem",
                  paddingTop: "10px",
                  paddingBottom: "105px",
                  flex: 1,
                  overflow: "auto",
                }}
              >
                <div className="">
                  <div className="row">
                    <div className="col-12">
                      <table className="" style={{ width: "100%" }}>
                        <tbody>
                          <tr>
                            <td className="text-left">
                              <strong>Site :</strong>
                            </td>
                            <td className="text-right">
                              {resolveSiteProduction(props.contrat)}
                            </td>
                            <td className="text-right"></td>
                            <td className="text-right"></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td></td>
                            <td></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td className="text-left">
                              <strong>N° Devis :</strong>
                            </td>
                            <td className="text-right" colSpan={2}>
                              {props.contrat.numeroDevis}
                            </td>
                            <td className="text-right"></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td className="text-left">
                              <strong>Effet :</strong>
                            </td>
                            <td className="text-right">
                              {frenchDate(props.contrat.dateEffet)}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-left">
                              <strong>N° Assuré</strong>
                            </td>
                            <td className="text-right">
                              {props.contrat.souscripteur.numeroAssureP}
                            </td>
                            <td className="text-right"></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td className="text-left">
                              <strong>Apporteur :</strong>
                            </td>
                            <td className="text-right" colSpan={2}>
                              {props.contrat.engins[0]?.concessionnaire
                                ? props.contrat.engins[0]?.concessionnaire
                                  ?.raisonSociale
                                : props.contrat?.intermediaire
                                  ? `${props.contrat?.intermediaire?.personne?.nom} ${props.contrat?.intermediaire?.personne?.prenom}`
                                  : props.contrat?.courtier?.nom}
                            </td>
                            <td className="text-right"></td>
                            <td>
                              <div className=""></div>
                            </td>
                            <td className="text-left">
                              <strong>Echéance :</strong>
                            </td>
                            <td className="text-right">
                              {frenchDateSimple(props.contrat.dateEcheance)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="row">
                    <div className="col-12 text-left" style={{ fontSize: "14px" }}>
                      {`Aux Conditions Générales de la Police qui précèdent et à celles particulières qui suivent, les Compagnies d'Assurances IARDT assurent le véhicule ci-dessous désigné suivant les garanties accordées. Ainsi, sont
                           nulles toutes adjonctions ou modifications matérielles
                           non revêtues du visa de la direction ou du
                           représentant.`}
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="text-center">
                    <h6 style={{ marginBottom: "0px" }}>Conditions Particulières - ASSURANCE AUTOMOBILE</h6>
                    <h7>
                      <strong>
                        <u>CONTRAT A DUREE FERME</u>
                      </strong>
                    </h7>
                  </div>
                </div>

                {/* Replace more GridContainers and GridItems with Bootstrap layout */}

                <div className="row">
                  <div className="col-12">
                    <table
                      // className="table table-borderless"
                      style={{ width: "100%" }}
                    >
                      <tbody>
                        <tr>
                          <td className="text-start">
                            <strong>SOUSCRIPTEUR : </strong>{" "}
                            {props.contrat.souscripteur.prenom
                              ? `${props.contrat.souscripteur.nom} ${props.contrat.souscripteur.prenom}`
                              : `${props.contrat.souscripteur.nom}`}{" "}
                            (<strong>ASSURE</strong>:{" "}
                            {props.contrat.assure.prenom
                              ? `${props.contrat.assure.nom} ${props.contrat.assure.prenom}`
                              : `${props.contrat.assure.nom}`}
                            )
                          </td>
                          <td className=""></td>
                          <td>{/* <div className="w-100"></div> */}</td>
                          <td className=""></td>
                          <td className="text-end">
                            <strong>PRODUCTEUR : </strong>
                            {props?.contrat?.producteur?.personne?.prenom}{" "}
                            {props?.contrat?.producteur?.personne?.nom}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <hr className="my-1" />
                    <table
                      style={{ width: "100%" }}
                    >
                      <tbody>
                        <tr>
                          <td className="text-start">
                            <strong>Profession : </strong>
                            {props.contrat.souscripteur.profession}
                          </td>
                          <td className=""></td>
                          <td>{/* <div className="w-100"></div> */}</td>
                          <td className=""></td>
                          <td className="text-end">
                            <strong>Adresse : </strong>
                            {props.contrat.souscripteur.adresse} {props.contrat?.souscripteur.contact ? `(${props.contrat?.souscripteur.contact[0]})` : ""}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <hr className="my-0" />
                    <div className="text-center" style={{ marginBottom: "-10px" }}>
                      <h6>
                        IDENTIFICATION VEHICULE - N° Immatriculation :{" "}
                        <strong>
                          {props.contrat.engins[0].immatriculation}
                        </strong>
                      </h6>
                      {/* Changement de véhicule : CP unique mentionnant l'engin remplacé. */}
                      {avenantCode === "CV" && props?.contrat?.enginRemplace && (
                        <p style={{ margin: "2px 0 0", fontSize: "13px", fontStyle: "italic", color: "#272366" }}>
                          En remplacement du véhicule : <strong>{props.contrat.enginRemplace}</strong>
                        </p>
                      )}
                    </div>
                    <hr className="my-0" />
                    <div class="">
                      <div class="row">
                        <div class="col-12">
                          <table style={{ width: "100%", fontSize: "14px", lineHeight: "1.5", borderCollapse: "collapse" }}>
                            <tbody>
                              <tr>
                                <td style={{ width: "12%", fontWeight: "bold", color: "black" }}>Marque :</td>
                                <td style={{ width: "20%", color: "#272366" }}>{props.contrat?.engins[0].marque}</td>
                                <td style={{ width: "12%", fontWeight: "bold", color: "black" }}>Carrosserie :</td>
                                <td style={{ width: "20%", color: "#272366" }}>{props.contrat?.engins[0].carosserie}</td>
                                <td style={{ width: "12%", fontWeight: "bold", color: "black" }}>Année :</td>
                                <td style={{ width: "10%", color: "#272366" }}>
                                  {props.contrat?.engins[0].dateMiseEnCirculation?.split("-")[0]}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: "bold", color: "black" }}>Puissance :</td>
                                <td style={{ color: "#272366" }}>
                                  {props.contrat?.engins[0].categorie.codeCategorie !== "M"
                                    ? `${props.contrat?.engins[0].puissance || props.contrat?.engins[0].volume} CV`
                                    : `${props.contrat?.engins[0].volume || props.contrat?.engins[0].puissance} cm³`}
                                </td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Énergie :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0].energie}</td>
                                <td style={{ fontWeight: "bold", color: "black" }}>N° Châssis :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0].numeroChassis}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: "bold", color: "black" }}>Valeur vénale :</td>
                                <td style={{ color: "#272366" }}>
                                  {displayNumbers(
                                    props.contrat?.engins[0].valeurVenale
                                  )} FCFA
                                </td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Places :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0].nombrePlace}</td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Genre :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0].genre}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: "bold", color: "black" }}>N° Moteur :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0].moteur}</td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Valeur à neuf :</td>
                                <td style={{ color: "#272366" }}>
                                  {displayNumbers(
                                    props.contrat?.engins[0].valeurANeuf
                                  )}
                                </td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Catégorie :</td>
                                <td style={{ color: "#272366" }}>{props.contrat?.engins[0]?.categorie?.abbreviation || props.contrat?.engins[0]?.categorie.nom}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: "bold", color: "black" }}>Usage :</td>
                                <td style={{ color: "#272366" }} colSpan="2">{props.contrat?.engins[0].usage}</td>
                                <td style={{ fontWeight: "bold", color: "black" }}>Lieu garage :</td>
                                <td colSpan="2" style={{ color: "#272366" }}>...</td>
                              </tr>
                            </tbody>
                          </table>

                          <hr className="my-2" />
                          <div className="">
                            {/* ===== TABLEAU DES GARANTIES ===== */}
                            <table
                              className="table table-bordered"
                              style={{
                                border: "1px solid black",
                                fontSize: "11px",
                              }}
                            >
                              <thead>
                                <tr>
                                  {[
                                    "Garanties",
                                    "Capitaux",
                                    ...(hasFractionnement
                                      ? ["Prime Annuelle (FCFA)", "Prime Fractionnée (FCFA)"]
                                      : ["Prime (En FCFA)"])
                                  ].map((label, i) => (
                                    <th
                                      key={i}
                                      style={{
                                        border: "1px solid black",
                                        textAlign: "center",
                                        padding: "4px",
                                        backgroundColor: hasFractionnement && i === 3 ? "#272366" : "#e6e9f8",
                                        color: hasFractionnement && i === 3 ? "#fff" : "#272366",
                                      }}
                                    >
                                      {label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>

                              <tbody>
                                {(() => {
                                  const garantiesTransformees = primes?.compulsoryGuaranties?.reduce((acc, gRaw) => {
                                    // Copie (jamais muter la source → évite l'accumulation au re-render).
                                    const g = { ...gRaw, nom: gRaw.nom == null ? "RC" : gRaw.nom };
                                    const isRC = g.nom?.toUpperCase()?.startsWith("RC") || g.nom?.toUpperCase()?.startsWith("SURPRIME");
                                    const nom = isRC ? "RC" : g.nom;
                                    // FUSION des lignes de MÊME garantie (payée + sa sous-garantie offerte)
                                    // → une seule ligne ; sous-garanties combinées ; capital valide conservé.
                                    const existing = acc.find((item) => item.nom === nom);
                                    if (existing) {
                                      existing.primeNetteAnnuelle = (existing.primeNetteAnnuelle || 0) + (g.primeNetteAnnuelle || 0);
                                      existing.primeNette = (existing.primeNette || 0) + (g.primeNette || 0);
                                      // Garantie AJOUTÉE (avenant AG) : on conserve le marqueur après fusion.
                                      existing.recentlyAdded = existing.recentlyAdded || g.recentlyAdded;
                                      existing.sousGaranties = [...(existing.sousGaranties || []), ...(g.sousGaranties || [])];
                                      const gCap = Number(g.capital);
                                      const exCap = Number(existing.capital);
                                      if ((existing.capital == null || isNaN(exCap) || exCap === 0) && !isNaN(gCap) && gCap !== 0) {
                                        existing.capital = g.capital;
                                      }
                                    } else {
                                      acc.push({ ...g, nom, sousGaranties: [...(g.sousGaranties || [])] });
                                    }
                                    return acc;
                                  }, []);

                                  const nbEcheances = primes?.fractionnement?.nombre || 1;

                                  return garantiesTransformees?.map((dt, id) => (
                                    <tr key={id} style={{ border: "1px solid black", lineHeight: dt.nom === "Individuel Conducteur" ? "1.1" : "1" }}>

                                      {/* Garantie */}
                                      <td style={{ border: "1px solid black", padding: "3px", verticalAlign: "top" }}>
                                        {dt.nom === "Individuel Conducteur" &&
                                          props?.contrat?.engins[0]?.categorie?.codeCategorie === "M" ? (
                                          <table style={{ fontSize: "10px", width: "100%" }}>
                                            <tbody>
                                              <tr><td colSpan="2">Individuel Conducteur</td></tr>
                                              <tr><td>* Décès :</td><td></td></tr>
                                              <tr><td>* Invalidité :</td><td></td></tr>
                                              <tr><td>* Frais Médicaux :</td><td></td></tr>
                                            </tbody>
                                          </table>
                                        ) : (
                                          <>
                                            {dt.nom}
                                            {/* Marqueur « garantie ajoutée » (avenant d'augmentation de garantie). */}
                                            {dt.recentlyAdded && (
                                              <span style={{ color: "#389e0d", fontWeight: 700, marginLeft: 4 }}>✚ nouvelle</span>
                                            )}
                                            {/* Sous-garanties nommées sous la garantie ; « Offerte » si prime 0. Pas sous la RC. */}
                                            {dt.nom !== "RC" && Array.isArray(dt.sousGaranties) && dt.sousGaranties.filter((sg) => sg?.nom && sg.nom !== dt.nom).length > 0 && (
                                              <table style={{ fontSize: "10px", width: "100%" }}>
                                                <tbody>
                                                  {dt.sousGaranties.filter((sg) => sg?.nom && sg.nom !== dt.nom).map((sg, i) => (
                                                    <tr key={i}>
                                                      <td>* {sg.nom}{((!sg.primeNette || Number(sg.primeNette) === 0) && !sg.tauxPCent && !sg.tauxPMille) ? " — Offerte" : ""}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            )}
                                          </>
                                        )}
                                      </td>

                                      {/* Capitaux */}
                                      <td style={{ border: "1px solid black", padding: "3px", textAlign: "right", verticalAlign: "top" }}>
                                        {dt.nom === "Individuel Conducteur" &&
                                          props?.contrat?.engins[0]?.categorie?.codeCategorie === "M" ? (
                                          <table style={{ fontSize: "10px", width: "100%" }}>
                                            <tbody>
                                              <tr><td></td><td>-</td></tr>
                                              <tr><td></td><td>250 000</td></tr>
                                              <tr><td></td><td>500 000</td></tr>
                                              <tr><td></td><td>50 000</td></tr>
                                            </tbody>
                                          </table>
                                        ) : (
                                          displayNumbers(
                                            // CEDEAO : capital FIGÉ à 4 000 000 000 quel que soit le capital en base
                                            // (souvent « illimité » → rendu ∞). RC : 4 000 000 000 seulement si vide.
                                            `${dt?.nom ?? ""} ${dt?.code ?? ""}`.toUpperCase().includes("CEDEAO")
                                              ? 4000000000
                                              : (dt.nom === "RC" && !dt.capital) ? 4000000000 : dt.capital)
                                        )}
                                      </td>

                                      {/* Prime Annuelle */}
                                      <td style={{ border: "1px solid black", padding: "3px", textAlign: "right", verticalAlign: "middle" }}>
                                        {displayNumbers(dt.primeNetteAnnuelle)}
                                      </td>

                                      {/* Prime Fractionnée — uniquement si fractionnement */}
                                      {hasFractionnement && (
                                        <td style={{
                                          border: "1px solid #272366",
                                          padding: "3px",
                                          textAlign: "right",
                                          verticalAlign: "middle",
                                          backgroundColor: "#f0f2fb",
                                          color: "#272366",
                                          fontWeight: "bold",
                                        }}>
                                          {displayNumbers(Math.round(dt.primeNette))}
                                        </td>
                                      )}
                                    </tr>
                                  ));
                                })()}
                                {/* Ligne de total — remplace le tfoot pour garantir l'ordre visuel */}
                                {hasFractionnement && (
                                  <tr>
                                    <td
                                      colSpan={2}
                                      style={{
                                        border: "1px solid black",
                                        padding: "3px",
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        backgroundColor: "#e6e9f8",
                                        color: "#272366",
                                      }}
                                    >
                                      Total primes nettes
                                    </td>
                                    <td style={{
                                      border: "1px solid black",
                                      padding: "3px",
                                      textAlign: "right",
                                      fontWeight: "bold",
                                      backgroundColor: "#e6e9f8",
                                      color: "#272366",
                                    }}>
                                      {displayNumbers(primeNetteAnnuelle)}
                                    </td>
                                    <td style={{
                                      border: "1px solid #272366",
                                      padding: "3px",
                                      textAlign: "right",
                                      fontWeight: "bold",
                                      backgroundColor: "#272366",
                                      color: "#fff",
                                    }}>
                                      {primeNetteFractionneeDisplay}
                                    </td>
                                  </tr>
                                )}
                                {!hasFractionnement && (
                                  <tr>
                                    <td
                                      colSpan={2}
                                      style={{
                                        border: "1px solid black",
                                        padding: "3px",
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        backgroundColor: "#e6e9f8",
                                        color: "#272366",
                                      }}
                                    >
                                      Total primes nettes
                                    </td>
                                    <td style={{
                                      border: "1px solid black",
                                      padding: "3px",
                                      textAlign: "right",
                                      fontWeight: "bold",
                                      backgroundColor: "#e6e9f8",
                                      color: "#272366",
                                    }}>
                                      {displayNumbers(primes?.primeNette)}
                                    </td>
                                  </tr>
                                )}
                              </tbody>

                              {/* Second tbody — récapitulatif des primes et taxes */}
                              <tbody>
                                {/* Durée + Prime Nette */}
                                <tr>
                                  <td style={{ border: "none", whiteSpace: "nowrap" }} colSpan={2}>
                                    <strong>
                                      Durée :{" "}
                                      {props?.contrat?.fractionnement
                                        ? `${props.contrat?.fractionnement.moisMin} Mois`
                                        : props?.contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                                          ? "15 JOURS"
                                          : "12 MOIS"}
                                    </strong>
                                  </td>
                                  <td style={{ border: "1px solid #ccc", padding: "3px", textAlign: "right", whiteSpace: "nowrap" }}>
                                    <span style={{ color: "#555" }}>Coût de pièces :</span>{" "}
                                    {displayNumbers(primes?.accessoires)}
                                  </td>
                                  {hasFractionnement && (
                                    <td style={{ border: "1px solid #272366", padding: "3px", textAlign: "right", whiteSpace: "nowrap", backgroundColor: "#f0f2fb" }}>
                                      <span style={{ color: "#555" }}>Coût de pièces fractionnées :</span>{" "}
                                      {displayNumbers(primes?.accessoiresFractionnee)}
                                    </td>
                                  )}
                                </tr>

                                {/* Réduction flotte + Prime Totale */}
                                <tr>
                                  <td style={{ border: "none", whiteSpace: "nowrap" }}>Réduction flotte :</td>
                                  <td style={{ border: "none", textAlign: "right", paddingRight: "8px" }}>
                                    {primes?.flotte ? `${primes?.flotte}%` : "0%"}
                                  </td>
                                  <td style={{ border: "1px solid #ccc", padding: "3px", textAlign: "right", whiteSpace: "nowrap" }}>
                                    <span style={{ color: "#555" }}>Taxes (6%) :</span>{" "}
                                    {displayNumbers(Math.round(primes?.taxes))}
                                  </td>
                                  {hasFractionnement && (
                                    <td style={{ border: "1px solid #272366", padding: "3px", textAlign: "right", whiteSpace: "nowrap", backgroundColor: "#f0f2fb" }}>
                                      <span style={{ color: "#555" }}>Taxes fractionnées (6%) :</span>{" "}
                                      {displayNumbers(Math.round(((primes?.taxesFractionnee))))}
                                    </td>
                                  )}
                                </tr>

                                {/* BNS + Coût pièces */}
                                <tr>
                                  <td style={{ border: "none", whiteSpace: "nowrap" }}>BNS :</td>
                                  <td style={{ border: "none", textAlign: "right", paddingRight: "8px" }}>
                                    {bonusMalusText}
                                  </td><td style={{
                                    border: "1px solid #272366",
                                    padding: "4px 5px",
                                    textAlign: "right",
                                    whiteSpace: "nowrap",
                                    backgroundColor: "#e6e9f8",
                                  }}>
                                    <strong style={{ color: "#272366" }}>
                                      PRIME TOTALE : {props?.contrat?.primeTotale && displayNumbers(Math.round(primeTotaleAnnuelle))}
                                    </strong>
                                  </td>
                                  {hasFractionnement && (
                                    <td style={{
                                      border: "1px solid #272366",
                                      padding: "4px 5px",
                                      textAlign: "right",
                                      whiteSpace: "nowrap",
                                      backgroundColor: "#272366",
                                      color: "#fff",
                                    }}>
                                      <strong>PRIME TOTALE FRACTIONNEE : {displayNumbers(Math.round(primes?.primeTotale))}</strong>
                                    </td>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                            <div class="row">
                              <div class="col-12">
                                <table class="table w-90">
                                  <tr>
                                    <td class="text-right">
                                      <strong style={{ color: "black" }}>
                                        LE SOUSCRIPTEUR
                                      </strong>
                                    </td>
                                    <td class="w-30"></td>
                                    <td>
                                      <div class="w-40"></div>
                                    </td>
                                    <td class="text-right">
                                      <strong style={{ color: "black" }}>
                                        DATE :
                                      </strong>
                                    </td>
                                    <td class="w-40"></td>
                                    <td>
                                      <div class="w-100"></div>
                                    </td>
                                    <td class="text-right">
                                      <strong style={{ color: "black" }}>
                                        POUR LA COMPAGNIE :
                                      </strong>
                                    </td>
                                    <td colspan="2" class="text-right"></td>
                                  </tr>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Continue with more Bootstrap code, replacing Grid and Material UI components */}
              </div>
              <div style={{ flexShrink: 0 }}>
                {props?.printing ? <p style={{ marginLeft: "15px" }} >Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}</p> : props?.contrat?.dateImpressionProposition ? <p style={{ marginLeft: "15px" }} >Imprimé le : {frenchDate(props?.contrat?.dateImpressionProposition)}</p> : null}
                <DocumentFooter />
              </div>
            </div>
          </Watermark>
        </div>
      ) : (
        <div class="col-12" ref={ref}>
          <Watermark content="PROPOSITION">
            <div className="card" style={{ position: "relative", minHeight: "100vh" }}>
              <AntidateBadge contrat={props.contrat} variant="inset" />
              <DuplicataStamp contrat={props.contrat} />
              <div style={{ flexShrink: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0px 0px",
                  }}
                >
                  {props?.contrat?.organisme?.typeOrganisme !== "POOL" && <Image
                    src={logoPreviewUrl}
                    style={{
                      width: "100%",
                      height: "105px",
                    }}
                  />}

                  <Image
                    src={entete}
                    style={{
                      width: "100%",
                      height: "105px",
                    }}
                  />
                </div>

                <BarreTop />
              </div>
              {/* --- Nouveau bloc pour le titre de l'avenant --- */}
              {props?.contrat?.avenant?.code && props?.contrat?.avenant?.code !== "AN" && (
                <div className="text-center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <h5 style={{ fontWeight: 'bold', color: '#272366' }}>
                    {props?.contrat?.avenant?.code === 'AG' && "AVENANT D'AUGMENTATION DE GARANTIE"}
                    {props?.contrat?.avenant?.code === 'DG' && "AVENANT DE DIMINUTION DE GARANTIE"}
                    {props?.contrat?.avenant?.code === 'RV' && "AVENANT DE REMISE EN VIGUEUR"}
                    {props?.contrat?.avenant?.code === 'RN' && "AVENANT DE RENOUVELLEMENT"}
                    {props?.contrat?.avenant?.code === 'SP' && "AVENANT DE SUSPENSION"}
                    {props?.contrat?.avenant?.code === 'RS' && "AVENANT DE RESILIATION"}
                    {props?.contrat?.avenant?.code === 'RE' && "AVENANT DE REPORT D'EFFET"}
                    {props?.contrat?.avenant?.code === 'CV' && "AVENANT DE CHANGEMENT DE VEHICULE"}
                    {props?.contrat?.avenant?.code === 'AO' && "AVENANT D'ORDRE"}
                    {props?.contrat?.avenant?.code === 'OE' && "AVENANT D'ORDRE"}
                    {/* Ajoutez d'autres conditions pour les codes d'avenant si nécessaire */}
                  </h5>
                  {shouldDisplayAvenantDate && (
                    <p style={{ marginBottom: '4px', color: '#272366', fontSize: '12px' }}>
                      Date d'effet de l'avenant : {frenchDate(avenantEffectiveDate)}
                    </p>
                  )}
                  <hr className="my-0" style={{ color: "black" }} />
                </div>
              )}
              {/* --- Fin du nouveau bloc --- */}

              <div
                style={{
                  padding: "1rem",
                  paddingTop: "10px",
                  paddingBottom: "105px",
                  flex: 1,
                  overflow: "auto",
                }}
              >
                <div class="text-center" style={{ marginBottom: "10px", marginTop: "-10px" }}>
                  <h5 className="my-0">
                    Conditions Particulières{" "}
                    {props.contrat?.engins[0]?.categorie?.nom} (Flotte)
                  </h5>
                </div>
                <hr className="my-0" style={{ color: "black" }} />
                <div className="row">
                  <div className="col-12">
                    {/* En-tête */}
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                      <h5 style={{ margin: 0, marginLeft: "25px", fontWeight: "bold" }}>Référence Souscripteur</h5>
                      <h5 style={{ margin: 0, marginRight: "145px", fontWeight: "bold" }}>Référence de la Police</h5>
                    </div>
                    <hr style={{ color: "black", margin: "8px 0" }} />

                    {/* Tableau sans bordures */}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", lineHeight: "2.2" }}>
                      <tbody>
                        <tr>
                          <td colSpan="2"> <span style={{ width: "12%", fontWeight: "bold", color: "black" }}>Code Assuré :</span>  {props.contrat?.souscripteur?.numeroAssureM ? props.contrat?.souscripteur?.numeroAssureM : props?.contrat?.souscripteur?.numeroAssureP}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>N° Quittance :</strong> -</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Emission :</strong> -</td>
                        </tr>
                        <tr>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Titre : </strong>{titreSouscripteur} </td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>N° Devis :</strong> {props.contrat?.numeroDevis}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Avenant :</strong> {props.contrat?.avenant?.nom}</td>
                        </tr>
                        <tr>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Nom :</strong> {props.contrat?.souscripteur?.nom} {props.contrat?.souscripteur?.prenom || ""}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Effet :</strong> {props.contrat?.dateEffet && frenchDateSimple(props.contrat?.dateEffet)}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Echéance :</strong> {props.contrat?.dateEcheance && frenchDateSimple(props.contrat?.dateEcheance)}</td>
                        </tr>
                        <tr>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Adresse :</strong> {props.contrat?.souscripteur?.adresse}</td>
                          <td colSpan="2">
                            <strong style={{ fontWeight: "bold", color: "black" }}>Catégorie :</strong>
                            {props.contrat?.engins?.[0]?.categorie?.nom}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Tel :</strong> {props.contrat?.souscripteur?.contact?.join(" / ")}</td>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Mouvement :</strong> {props.contrat?.avenant?.nom}</td>
                        </tr>
                        <tr>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Email :</strong> {props.contrat?.souscripteur?.email || "-"}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Ecriture :</strong> {bareme}</td>
                          <td><strong style={{ fontWeight: "bold", color: "black" }}>Durée :</strong> {props?.contrat?.fractionnement
                            ? `${props.contrat?.fractionnement.moisMin} Mois`
                            : props?.contrat?.engins[0]?.categorie?.codeCategorie?.startsWith("V")
                              ? "15 JOURS"
                              : "12 MOIS"}</td>
                        </tr>
                        <tr>
                          <td colSpan="2">
                            <strong style={{ fontWeight: "bold", color: "black" }}>Site de production :</strong>
                            {` ${resolveSiteProduction(props.contrat)}`}
                          </td>
                          <td colSpan="2"><strong style={{ fontWeight: "bold", color: "black" }}>Type emission :</strong> SANS TACITE RECONDUCTION</td>
                        </tr>
                        <tr>
                          <td colSpan="2">
                            <strong style={{ fontWeight: "bold", color: "black" }}>Intermédiaire :</strong>
                            {props.contrat?.intermediaire
                              ? `${props.contrat?.intermediaire?.personne?.nom} ${props.contrat?.intermediaire?.personne?.prenom}`
                              : props.contrat?.courtier?.nom || "-"}
                          </td>
                          <td colSpan="2">
                            <strong style={{ fontWeight: "bold", color: "black" }}>Producteur :</strong> {props?.contrat?.producteur?.personne?.nom} {props?.contrat?.producteur?.personne?.prenom}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <hr className="my-2" style={{ color: "black" }} />
                    <div className="row">
                      <div className="col-12 text-left" style={{ fontSize: "15px" }}>
                        {(props?.contrat?.avenant?.code === 'AN' || props?.contrat?.avenant?.code === 'RN') && `Aux Conditions Générales de la Police qui précèdent et à celles particulières qui suivent, les Compagnies d'Assurances IARDT assurent le véhicule ci-dessous désigné suivant les garanties accordées. Ainsi, sont
                          nulles toutes adjonctions ou modifications matérielles
                          non revêtues du visa de la direction ou du
                          représentant.`}
                        {props?.contrat?.avenant?.code === 'SP' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci-dessus référencée sont suspendues pour une durée maximum d'un (01) an à compter de la date de suspension.
                                                            Si l’assuré ne remet pas l’assurance en vigueur avant l’expiration d'un (01) an, la Police susvisée sera résiliée de plein droit. `}
                        {props?.contrat?.avenant?.code === 'RE' && `D’un commun accord entre les parties, l’effet du contrat ci-dessus est reporté à la date effet sus-mentionnée.
                                                            Ledit contrat cours désormais jusqu’ à la date échéance ci-dessus. `}
                        {props?.contrat?.avenant?.code === 'RV' && `A la demande de l’assuré et d’un commun accord entre les parties, les garanties de la Police ci-dessus référencée sont remises en vigueur à compter du [%RUB_Effet%].
                                                            L’échéance est fixée au [%RUB_Expiration%]. `}
                        {props?.contrat?.avenant?.code === 'IN' && `D’un commun accord entre les parties il est convenu que les véhicules ci-joints sont incorporés au présent contrat aux conditions mentionnées ci-dessus. 
                                                            L’assuré s’engage à payer à l’assureur la prime totale ci-dessus décomptée.`}
                        {props?.contrat?.avenant?.code === 'RS' && `A la demande de l’assuré et d’un commun accord entre les parties, la Police sus-mentionnée faisant l’objet du présent avenant est résiliée à compter du [%RUB_Effet%] et cesse dès lors de produire ses effets à l’égard des parties. En conséquence la compagnie s’engage à ristourner à l’assuré la portion de prime correspondant à la période non courue.`}
                      </div>
                    </div>
                    <hr className="my-2" style={{ color: "black" }} />
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col-12">
                    <div className="card p-2">
                      {/* Compact Table */}
                      <table
                        className="table table-sm table-bordered mb-3"
                        style={{
                          border: "1px solid black",
                          fontSize: "12px",
                          borderCollapse: "collapse",
                        }}
                      >
                        <tbody>
                          {reduData?.map((rd, id) => (
                            <tr key={id}>
                              {/* Reduction Label */}
                              <td
                                style={{
                                  border: "1px solid black",
                                  textAlign: "center",
                                  padding: "2px 4px",
                                  backgroundColor: rd.highlight ? "#fff3cd" : "#e6e9f8",
                                  color: rd.highlight ? "#7a5b00" : "#272366",
                                  fontWeight: "700",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {rd.libelle}
                              </td>

                              {/* Reduction Montant */}
                              <td
                                style={{
                                  border: "1px solid black",
                                  textAlign: rd.align || "right",
                                  verticalAlign: "middle",
                                  padding: "2px 6px",
                                  fontWeight: "700",
                                  backgroundColor: rd.highlight ? "#fff3cd" : undefined,
                                  color: rd.highlight ? "#7a5b00" : undefined,
                                }}
                              >
                                {rd.montant}
                              </td>

                              {/* Prime Label */}
                              <td
                                style={{
                                  border: primeData[id]?.empty ? "none" : "1px solid black",
                                  textAlign: "center",
                                  padding: "2px 4px",
                                  backgroundColor: primeData[id]?.empty ? "transparent" : "#e6e9f8",
                                  color: "#272366",
                                  fontWeight: "700",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {primeData[id]?.libelle}
                              </td>

                              {/* Prime Montant */}
                              <td
                                style={{
                                  border: primeData[id]?.empty ? "none" : "1px solid black",
                                  textAlign: "right",
                                  verticalAlign: "middle",
                                  padding: "2px 6px",
                                  fontWeight: "700",
                                }}
                              >
                                {primeData[id]?.montant}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Signature / Date Section */}

                    </div>
                    <div className="row">
                      <table
                        className="table table-borderless table-sm w-100"
                        style={{ fontSize: "12px" }}
                      >
                        <tbody>
                          <tr>
                            <td className="text-right fw-bold text-dark">LE SOUSCRIPTEUR</td>
                            <td></td>
                            <td></td>
                            <td className="text-right fw-bold text-dark">DATE :</td>
                            <td></td>
                            <td></td>
                            <td className="text-right fw-bold text-dark">
                              POUR LA COMPAGNIE :
                            </td>
                            <td className="text-right"></td>
                          </tr>
                          <tr>
                            <td className="text-right fw-bold text-dark"></td>
                            <td></td>
                            <td></td>
                            <td>{frenchDate(date)}</td>
                            <td className="text-right fw-bold text-dark"></td>
                            <td></td>
                            <td className="text-right">
                              {imagePreviewUrl && (
                                <img
                                  src={imagePreviewUrl}
                                  height="80"
                                  alt="Signature"
                                  style={{ objectFit: "contain" }}
                                />
                              )}
                            </td>
                            <td className="text-right fw-bold text-dark"> </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {/* Footer Image */}
                    {/* <Image src={footer} alt="Footer Image" className="mt-2" /> */}
                  </div>
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                {props?.printing ? <p style={{ marginLeft: "15px" }} >Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}</p> : props?.contrat?.dateImpressionProposition ? <p style={{ marginLeft: "15px" }} >Imprimé le : {frenchDate(props?.contrat?.dateImpressionProposition)}</p> : null}
                <DocumentFooter />
              </div>
            </div>
            {/* ── ANNEXE FLOTTE (paysage) : caractéristiques engins + garanties en colonnes ── */}
            {avenantCode === "IN" ? (
              enginsIncorpores.length > 0 ? (
                <>
                  <AnnexeFlotte
                    engins={enginsIncorpores}
                    numeroPolice={annexeNumeroPolice}
                    assure={annexeAssure}
                    logoPreviewUrl={logoPreviewUrl}
                    showLogo={annexeShowLogo}
                    titreEngins="ANNEXE 1 INCORPORATION : Les engins"
                    titreGaranties="ANNEXE 2 INCORPORATION : Les garanties souscrites"
                    prorataFactor={Number(primes?.prorata) > 0 ? Number(primes.prorata) : 1}
                  />
                  <AnnexeFlotte
                    engins={enginsFlotte}
                    numeroPolice={annexeNumeroPolice}
                    assure={annexeAssure}
                    logoPreviewUrl={logoPreviewUrl}
                    showLogo={annexeShowLogo}
                    titreEngins="ANNEXE 3 SITUATION DE LA FLOTTE : Les engins"
                    titreGaranties="ANNEXE 4 SITUATION DE LA FLOTTE : Les garanties souscrites"
                  />
                </>
              ) : (
                <AnnexeFlotte
                  engins={enginsFlotte}
                  numeroPolice={annexeNumeroPolice}
                  assure={annexeAssure}
                  logoPreviewUrl={logoPreviewUrl}
                  showLogo={annexeShowLogo}
                  titreEngins="ANNEXE 1 SITUATION DE LA FLOTTE : Les engins"
                  titreGaranties="ANNEXE 2 SITUATION DE LA FLOTTE : Les garanties souscrites"
                />
              )
            ) : (
              <AnnexeFlotte
                engins={enginsFlotte}
                numeroPolice={annexeNumeroPolice}
                assure={annexeAssure}
                logoPreviewUrl={logoPreviewUrl}
                showLogo={annexeShowLogo}
              />
            )}
          </Watermark>
        </div>
      )}
    </>
  );
});

DTRFFormatProposition.propTypes = {
  contrat: PropTypes.object,
};
export default DTRFFormatProposition;
